/**
 * This module links PeerNodes and Stateful objects such that properties set on the Stateful object are
 * forwarded to the peer node and conversely properties set on the peer node are forwarded to the stateful object.
 * It also provide methods for constructing ChildProperties or DocumentTypeProperties objects from PeerNode objects
 * and vise versa.
 */
define([
    "MW/uiframework/UIContainer",
    "MW/uiframework/uicontainer/ChildProperties",
    "MW/uiframework/uicontainer/DocumentTypeProperties",
    "MW/uiframework/uicontainer/peer/PropertyTranslator"
], function (UIContainer, ChildProperties, DocumentTypeProperties, PropertyTranslator) {

    var JSON_PREFIX = "**JSON**";
    var PREFIX_LENGTH = JSON_PREFIX.length;

    var childPropertyNames = [
        "panelId",
        "documentId",
        "documentType",
        "childType",
        ChildProperties.ACTIVE_CONTEXTS,
        ChildProperties.DESCRIPTION,
        ChildProperties.INDEX,
        ChildProperties.IS_COLLAPSED,
        ChildProperties.IS_COLLAPSIBLE,
        ChildProperties.IS_DOCKED,
        ChildProperties.IS_MAXIMIZABLE,
        ChildProperties.IS_MAXIMIZED,
        ChildProperties.IS_OPEN,
        ChildProperties.IS_PHANTOM,
        ChildProperties.IS_RESIZABLE,
        ChildProperties.IS_SELECTED,
        ChildProperties.IS_SHOWING,
        ChildProperties.PERMISSIBLE_REGIONS,
        ChildProperties.PREFERRED_WIDTH,
        ChildProperties.PREFERRED_HEIGHT,
        ChildProperties.REGION,
        ChildProperties.TILE,
        ChildProperties.TITLE,
        ChildProperties.WINDOW_BOUNDS
    ];

    var documentTypePropertyNames = [
        "title",
        "collectiveLabel",
        "constrainToSubContainer",
        "retainEmptySubContainer",
        DocumentTypeProperties.DOCUMENT_COUNT,
        DocumentTypeProperties.LAST_SELECTED,
        DocumentTypeProperties.IS_DOCKED,
        DocumentTypeProperties.IS_MAXIMIZED,
        DocumentTypeProperties.SHRINK_TABS_TO_FIT,
        DocumentTypeProperties.SUB_GRID_SIZE,
        DocumentTypeProperties.TILE,
        DocumentTypeProperties.WINDOW_BOUNDS
    ];

    var containerPropertyNames; // Lazily initialize to get around circular dependency among this module UIContainer and UIContainerPeer
    var echoPropertyNames;      // Lazily initialize for reason given above
    var tileCoverageTimer;

    var childPropertyTranslators = {
        activeContexts: PropertyTranslator.ForceArray
    };

    return {

        link: function (peerNode, stateful, translators) {
            if (!echoPropertyNames) {
                echoPropertyNames = [
                    UIContainer.SELECTED_CHILD,
                    UIContainer.SELECTED_TOOLSTRIP_TAB,
                    UIContainer.LAYOUT,
                    UIContainer.DOCUMENT_LAYOUT,
                    UIContainer.PANEL_LAYOUT
                ];
            }
            var that = this;
            var forwardingFromPeer;
            var forwardingFromStateful;
            var peerPropertySetCallback = function (event) {
                var name, newValue;
                if (!forwardingFromStateful) {
                    name = event.data.key;
                    if (name === "tileCoverage" && tileCoverageTimer) {
                        clearTimeout(tileCoverageTimer);
                        tileCoverageTimer = null;
                    }
                    newValue = that.convertToObjectIfJson(event.data.newValue);
                    if (translators && translators[name]) {
                        newValue = translators[name].fromPeer(newValue);
                    }
                    forwardingFromPeer = name;
                    stateful.set(name, newValue);
                    forwardingFromPeer = null;
                }
            };
            peerNode.addEventListener("propertySet", peerPropertySetCallback);
            var forwardToPeerModel = function (name, newValue) {
                forwardingFromStateful = name;
                peerNode.setProperty(name, that.convertToJsonIfObject(newValue));
                forwardingFromStateful = null;
            };
            var propertyWatch = stateful.watch(function(name, oldValue, newValue) {
                var okToForward;

                // The "forwarding" flags prevent ringing but certain properties need to be echoed from the
                // client side since the server (peer) side may supply partial information.
                if ((forwardingFromPeer !== name || echoPropertyNames.indexOf(name) >= 0) && typeof name === "string") {

                    if (translators && translators[name]) {
                        newValue = translators[name].toPeer(newValue);
                        okToForward = !!newValue;
                    } else {
                        okToForward = that.isValueTransferrable(newValue);
                    }
                    if (okToForward) {
                        // The special treatment of tileCoverage below is needed to handle the case when the grid size
                        // and coverage are set in succession on the server (MATLAB) side.  Setting the grid size changes the
                        // tile coverage.  The following defers the response to the tile coverage change so that it doesn't
                        // countermand the setting from the server (MATLAB) side.
                        if (name !== "tileCoverage") {
                            forwardToPeerModel(name, newValue);
                        } else {
                            if (tileCoverageTimer) {
                                clearTimeout(tileCoverageTimer);
                            }
                            tileCoverageTimer = setTimeout(function() {
                                forwardToPeerModel(name, newValue);
                                tileCoverageTimer = null;
                            }, 1000);
                        }
                    }
                }
            });
            peerNode._removeBridgePropertiesLink = function () {
                peerNode.removeEventListener("propertySet", peerPropertySetCallback);
                propertyWatch.remove();
            };
        },

        unlink: function (peerNode) {
            if (peerNode._removeBridgePropertiesLink) {
                peerNode._removeBridgePropertiesLink();
                delete peerNode._removeBridgePropertiesLink;
            }
        },

        isValueTransferrable: function (value) {
            var i, keys;
            var valueType = typeof value;
            if (valueType === "string" || valueType === "number" || valueType === "boolean") {
                return true;
            }
            if (value && Array.isArray(value)) {
                for (i = 0; i < value.length; i++) {
                    if (!this.isValueTransferrable(value[i])) {
                        return false;
                    }
                }
                return true;
            }
            if (value && valueType === "object") {
                keys = Object.keys(value);
                for (i = 0; i < keys.length; i++) {
                    if (!this.isValueTransferrable(value[keys[i]])) {
                        return false;
                    }
                }
                return true;
            }
            return false;
        },

        createChildPropertiesFromPeerNode: function (peerNode) {
            var properties = new ChildProperties(peerNode.getProperties());
            if (properties.permissibleRegions) {
                properties.permissibleRegions = PropertyTranslator.ForceArray.fromPeer(properties.permissibleRegions)
            }
            this.link(peerNode, properties, childPropertyTranslators);
            return properties;
        },

        createPeerNodeFromChildProperties: function (parentPeerNode, childType, properties) {
            var filteredProperties = this._filter(properties, childPropertyNames);
            this._translatePropertiesToPeer(filteredProperties, childPropertyTranslators);
            var peerNode = parentPeerNode.addChild(childType, filteredProperties);
            this.link(peerNode, properties, childPropertyTranslators);
            return peerNode;
        },

        createDocumentTypePropertiesFromPeerNode: function (peerNode, translators) {
            var that = this;
            var properties = DocumentTypeProperties(peerNode.getProperties());
            Object.keys(properties).forEach(function (propertyName) {
                properties[propertyName] = that.convertToObjectIfJson(properties[propertyName]);
            });
            if (properties.contexts) {
                properties.contexts = PropertyTranslator.ForceArray.fromPeer(properties.contexts);
            }
            if (properties.panelTags) {
                properties.panelTags = PropertyTranslator.ForceArray.fromPeer(properties.panelTags);
            }
            if (properties.tabGroupTags) {
                properties.tabGroupTags = PropertyTranslator.ForceArray.fromPeer(properties.tabGroupTags);
            }
            this.link(peerNode, properties, translators);
            return properties;
        },

        createPeerNodeFromDocumentTypeProperties: function (parentPeerNode, typeId, properties, propertyTranslators) {
            var filteredProperties = this._filter(properties, documentTypePropertyNames);
            filteredProperties.typeId = typeId;
            this._translatePropertiesToPeer(filteredProperties, propertyTranslators);
            var peerNode = parentPeerNode.addChild("documentType", filteredProperties);
            this.link(peerNode, properties, propertyTranslators);
            return peerNode;
        },

        createPeerNodeFromUIContainer: function (peerModelManager, uiContainer, propertyTranslators) {
            if (!containerPropertyNames) {
                containerPropertyNames = [
                    UIContainer.BOTTOM_HEIGHT,
                    UIContainer.DOCUMENT_COLUMN_WEIGHTS,
                    UIContainer.DOCUMENT_GRID_SIZE,
                    UIContainer.DOCUMENT_LAYOUT,
                    UIContainer.DOCUMENT_PLACE_HOLDER_TEXT,
                    UIContainer.DOCUMENT_ROW_WEIGHTS,
                    UIContainer.DOCUMENT_TAB_POSITION,
                    UIContainer.DOCUMENT_TILE_COVERAGE,
                    UIContainer.IS_BOTTOM_COLLAPSED,
                    UIContainer.IS_BUSY,
                    UIContainer.IS_LEFT_COLLAPSED,
                    UIContainer.IS_RIGHT_COLLAPSED,
                    UIContainer.IS_TOOLSTRIP_COLLAPSED,
                    UIContainer.IS_VISIBLE,
                    UIContainer.LAST_SELECTED_DOCUMENT,
                    UIContainer.LAYOUT,
                    UIContainer.LEFT_WIDTH,
                    UIContainer.PANEL_LAYOUT,
                    UIContainer.RIGHT_WIDTH,
                    UIContainer.SELECTED_CHILD,
                    UIContainer.SELECTED_TOOLSTRIP_TAB,
                    UIContainer.TITLE,
                    UIContainer.WINDOW_BOUNDS
                ];
            }
            var filteredProperties = this._filter(uiContainer, containerPropertyNames);
            this._translatePropertiesToPeer(filteredProperties, propertyTranslators);
            var peerNode = peerModelManager.setRoot("containerRoot", filteredProperties);
            this.link(peerNode, uiContainer, propertyTranslators);
            return peerNode;
        },

        _filter: function (properties, propertyNames) {
            var filteredProperties = {};
            var i, propertyName, propertyValue;
            for (i = 0; i < propertyNames.length; i++) {
                propertyName = propertyNames[i];
                propertyValue = properties.get(propertyName);
                if (propertyValue !== undefined) {
                    filteredProperties[propertyName] = propertyValue;
                }
            }
            return filteredProperties;
        },

        _translatePropertiesToPeer: function (properties, translators) {
            if (!translators) {
                return;
            }
            var names = Object.keys(properties);
            for (var i = 0; i < names.length; i++) {
                var name = names[i];
                if (translators[name]) {
                    properties[name] = translators[name].toPeer(properties[name]);
                }
            }
        },

        convertToObjectIfJson: function (value) {
            return (typeof value === "string" && value.substr(0, PREFIX_LENGTH) === JSON_PREFIX) ?
                   JSON.parse(value.substr(PREFIX_LENGTH)) : value;
        },

        convertToJsonIfObject: function (value) {
            return typeof value === "object" ? (JSON_PREFIX + JSON.stringify(value)) : value;
        }
    };
});